import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { public: true, chrome: false },
  },
  {
    path: '/',
    redirect: '/overview',
  },
  {
    path: '/overview',
    name: 'overview',
    component: () => import('@/views/NetworkOverviewView.vue'),
    meta: { title: 'Network Overview Dashboard' },
  },
  // Kept so existing bookmarks and the pre-rename demo links still resolve.
  { path: '/fleet', redirect: '/overview' },
  {
    path: '/sites/:id',
    component: () => import('@/views/SiteLayout.vue'),
    props: true,
    children: [
      {
        path: '',
        name: 'site-chain',
        component: () => import('@/views/ReceiveChainView.vue'),
        meta: { title: 'Receive Chain' },
      },
      {
        path: 'spectrum',
        name: 'site-spectrum',
        component: () => import('@/views/SpectrumView.vue'),
        meta: { title: 'Spectrum' },
      },
      {
        path: 'trends',
        name: 'site-trends',
        component: () => import('@/views/TrendsView.vue'),
        meta: { title: 'Trends' },
      },
    ],
  },
  {
    path: '/alarms',
    name: 'alarms',
    component: () => import('@/views/AlarmsView.vue'),
    meta: { title: 'Alarms' },
  },
  {
    path: '/settings',
    name: 'settings',
    component: () => import('@/views/SettingsView.vue'),
    // Commissioning and user administration are operator work.
    meta: { title: 'Settings', requiresOperator: true },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { title: 'Not found' },
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 }),
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();

  if (!auth.isAuthenticated) {
    // One restore attempt per navigation into a guarded route; the API call is
    // cheap and only happens when tokens exist.
    await auth.restore();
  }

  if (to.meta.public) {
    return auth.isAuthenticated && to.name === 'login' ? { name: 'overview' } : true;
  }

  if (!auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } };
  }

  if (to.meta.requiresOperator && !auth.isOperator) {
    return { name: 'overview' };
  }

  return true;
});

router.afterEach((to) => {
  const title = to.meta.title as string | undefined;
  document.title = title ? `${title} · RF-SDAS` : 'RF-SDAS';
});
